import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import SubHeader from 'components/SubHeader';
import FooterBar from 'components/FooterBar';
import Card from 'components/Card';
import CircularProgress from '@mui/material/CircularProgress';
import ModalAddStaff from 'screens/ModalAddStaff';
import { useQuery } from '@apollo/client';
import { GET_ALL_STAFFS } from 'graphql/service';
import useWindowDimensions from 'customHook/useWindowDimensions';
import { EditIcon, RemoveIcon } from 'assets/icons';
import moment from 'moment-timezone';
import { TIME_ZONE } from 'constants/constant';
import {
  StaffsContainer,
  Container, 
  StyledInput,
  StyledTable,
  StyledItem,
  Navigator,
  NavigatorContainer
} from './styles';
import { debouncedErrorMessage } from 'utils/messageUtil';
import ModalRemoveStaff from 'screens/ModalRemoveStaff';

const Staff = () => {

  const { width, height } = useWindowDimensions();
  const [ keyword, setKeyword ] = useState('')
  const [ staffs, setStaffs ] = useState([])

  const { data: staffsData, loading, error, refetch } = useQuery(
    GET_ALL_STAFFS,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true
    }
  )

  useEffect(() => {
    if(staffsData) {
      setStaffs(staffsData.validStaff)
    }
  }, [staffsData])

  useEffect(() => {
    if (!error) return;

    if (!error.networkError) {
      let content = error.message;

      if (error.message.includes('Validation')) {
        content = JSON.stringify(
          error.graphQLErrors[0]?.extensions?.exception
        );
      }

      debouncedErrorMessage(content);
    }
  }, [error]);

  let pageSize
  if(width>1200) {
    pageSize = Math.floor((height - 260)/36)
  } else if(width<=1200 && width>767) {
    pageSize = Math.floor((height - 280)/48)
  } else {
    pageSize = Math.floor((height - 265)/48)
  }

  const inputHandler = (keyword) => {

    setKeyword(keyword)
    const filteredStaffs = staffsData.staffs?.filter((staff)=>
      (
        staff.user.fullName.includes(keyword) ||
        staff.user.phoneNumber.includes(keyword) ||
        staff.user.email.includes(keyword) ||
        staff.roles?.[0]?.name.includes(keyword)
      )
    ) || []
    setStaffs(filteredStaffs);
  }

  const handleKeyDown = (e) => {
    if(e.key === 'Enter') {
      if(e.target.value > 0 && e.target.value <= Math.ceil(staffs.length/pageSize)) {
        setPageNumber(e.target.value)
      } 
    }
  }

  const [pageNumber, setPageNumber] = useState(1)
  const [inputNumber, setInputNumber] = useState(pageNumber)

  const paginate = (array, page_size, page_number) => {
    const newArray = [...array]
    // human-readable page numbers usually start with 1, so we reduce 1 in the first argument
    return newArray.slice((page_number - 1) * page_size, page_number * page_size);
  }

  const previousPage = () => {
    setPageNumber(pageNumber-1)
    setInputNumber(pageNumber-1)
  }

  const nextPage = () => {
    setPageNumber(pageNumber+1)
    setInputNumber(pageNumber+1)
  }

  const resultArray = paginate(staffs, pageSize, pageNumber).map((staff, index)=> {

    return (
      <StyledItem key={index}>
        <td className="name">{staff.user.fullName}</td>
        <td className="role">{staff.roles?.[0]?.name}</td>
        <td className="phone">
          <a href={`tel:${staff.user.phoneNumber}`}>{staff.user.phoneNumber}</a>
        </td>
        <td className="email">{staff.user.email}</td>
        <td className="status">{staff.isActive ? "ACTIVE" : "INACTIVE"}</td>
        <td className="created">
          {moment(staff.user.createdAt).tz(TIME_ZONE).format("MMM Do YYYY")}
        </td>
        <td className="lastLogin">
          {/* FIXME: this lastLogin does not work */}
          {moment(staff.user.lastLogin).tz(TIME_ZONE).format("MMM Do YYYY")}
        </td>
        <td className="action">
          <div className="iconContainer">
            <Link
              className="icon"
              to={`/staff-details/${staff.staffId}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Edit staff details"
            >
              <EditIcon />
            </Link>
            <ModalRemoveStaff
              buttonIcon={<RemoveIcon />}
              iconStyle={`
                svg {
                  filter: brightness(0) invert(47%) sepia(100%) saturate(1000%) hue-rotate(315deg);
                }
              `}
              name={staff.user.fullName}
              staffId={staff.staffId}
              refetch={refetch}
            />
          </div>
        </td>
      </StyledItem>
    );
  })

  return (
    <>
      <Helmet>
        <title>Staff - Rook Hotel PMS</title>
        <meta name="description" content="Staff - Rook Hotel PMS" />
      </Helmet>
      <SubHeader>
        <div className="title">Staff</div>
        <div className="buttonContainer">
          <ModalAddStaff
            buttonLabel="Add Staff"
            buttonStyle={`
              width: 88px;
            `}
            setStaffs={setStaffs}
          />
        </div>
      </SubHeader>
      <StaffsContainer>
        <Container>
          {loading ? (
            <CircularProgress
              style={{ position: "absolute", top: "33px", left: "240px" }}
              color="success"
              thickness={10}
              size={20}
            />
          ) : (
            <Card>
              <StyledInput
                placeholder="Search..."
                value={keyword}
                onChange={(e) => inputHandler(e.target.value)}
              />
              <StyledTable>
                <thead>
                  <tr>
                    <th className="name">NAME</th>
                    <th className="role">ROLE</th>
                    <th className="phone">PHONE</th>
                    <th className="email">EMAIL</th>
                    <th className="status">STATUS</th>
                    <th className="created">CREATED</th>
                    <th className="lastLogin">LAST LOGIN</th>
                    <th className="action">ACTION</th>
                  </tr>
                </thead>
                <tbody>{resultArray}</tbody>
              </StyledTable>
            </Card>
          )}
          {staffs.length > pageSize ? (
            <NavigatorContainer>
              <div className="totalResults">{staffs.length} RESULTS</div>
              <Navigator
                disabled={pageNumber <= 1}
                onClick={previousPage}
              >
                {"<"} Back
              </Navigator>
              <div className="pageControl">
                <input
                  type="number"
                  value={inputNumber}
                  onChange={(e) => {
                    setInputNumber(e.target.value);
                  }}
                  onKeyDown={handleKeyDown}
                />
                <div className="totalPages">
                  {" "}
                  of {Math.ceil(staffs.length / pageSize)}
                </div>
              </div>
              <Navigator
                disabled={pageNumber >= staffs.length / pageSize}
                onClick={nextPage}
              >
                Next {">"}
              </Navigator>
            </NavigatorContainer>
          ) : null}
        </Container>
      </StaffsContainer>
      <FooterBar />
    </>
  );
}

export default Staff;
